import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import type {
  ImageDimensions,
  ImagePosition,
  PositionHandler,
} from 'hb-builder/components/BuilderComponents/single/PageImage/types';
import {
  useBuilderContext,
  useComponentContext,
  useDeviceModeContext,
} from 'hb-builder/hooks';
import type { PageImageItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/pageImageItemAttributess';

import Handlers from './Handlers';

const BORDER_WIDTH = 2;
const IMAGE_MIN_SIZE = 40;

interface ResizableImageProps {
  dimensions: Pick<PageImageItemAttributes, 'height' | 'width'>;
  isResizing: boolean;
  onChange: (value: {
    resizeDirection: string;
    value: ImagePosition & Pick<PageImageItemAttributes, 'height' | 'width'>;
  }) => void;
  position: ImagePosition;
  positionsHandler?: PositionHandler[];
  wrapperDimensions: { wrapperHeight: number; wrapperWidth: number };
}

const ResizableImage = ({
  dimensions,
  isResizing,
  onChange,
  position,
  positionsHandler,
  wrapperDimensions: { wrapperHeight, wrapperWidth },
}: ResizableImageProps) => {
  const { resizedItemId, setResizedItemId } = useBuilderContext();
  const { item } = useComponentContext();
  const { isDesktopMode } = useDeviceModeContext();

  const handleInitialRef = useRef<ImagePosition>();
  const handlersRef = useRef<Record<PositionHandler, HTMLDivElement> | {}>({});
  const initialValueRef = useRef<ImageDimensions>();
  const positionResizingRef = useRef<string>();
  const resizeRef = useRef<HTMLDivElement | null>(null);

  const [activeHandle, setActiveHandle] = useState<PositionHandler | null>(
    null,
  );
  const [isResizeActive, setIsResizeActive] = useState(false);
  const [resizeValues, setResizeValues] = useState<
    ImagePosition & Pick<PageImageItemAttributes, 'height' | 'width'>
  >({
    ...dimensions,
    ...position,
  });

  useEffect(() => {
    setResizeValues({ ...dimensions, ...position });
  }, [dimensions, position]);

  const handleMouseLeave = () => {
    if (isResizing) {
      setIsResizeActive(false);
    }
  };

  const handleMouseOver = () => setIsResizeActive(true);

  const handleMouseDown = (positionHandler: PositionHandler) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    setActiveHandle(positionHandler);
    setResizedItemId(item.id);

    if (resizeRef.current) {
      const { height, width } = resizeRef.current.getBoundingClientRect();
      const { y, x } = handlersRef?.current?.[
        positionHandler
      ]?.getBoundingClientRect?.() || { x: 0, y: 0 };

      initialValueRef.current = { height, width };
      handleInitialRef.current = { x, y };
      positionResizingRef.current = positionHandler;

      attachDocumentEvents();
    }
  };

  const handleMouseMove = (e) => {
    const diff = {
      height: handleInitialRef?.current?.y
        ? e.clientY - handleInitialRef.current.y
        : e.clientY,
      width: handleInitialRef?.current?.x
        ? e.clientX - handleInitialRef.current.x
        : e.clientX,
      x: handleInitialRef?.current?.x
        ? e.clientX - handleInitialRef.current.x + resizeValues.x
        : e.clientX,
      y: handleInitialRef?.current?.y
        ? e.clientY - handleInitialRef.current.y + resizeValues.y
        : e.clientY,
    };

    const newHeight = Math.floor(
      (initialValueRef?.current?.height || 0) -
        (positionResizingRef?.current?.includes('top')
          ? diff.height
          : positionResizingRef?.current?.includes('bottom')
          ? -diff.height
          : 0),
    );
    const newWidth = Math.floor(
      (initialValueRef?.current?.width || 0) -
        (positionResizingRef?.current?.includes('left')
          ? diff.width
          : positionResizingRef?.current?.includes('right')
          ? -diff.width
          : 0),
    );

    setResizeValues((prev) => {
      const lockedHeight =
        newHeight > IMAGE_MIN_SIZE ? newHeight : IMAGE_MIN_SIZE;
      const lockedWidth = newWidth > IMAGE_MIN_SIZE ? newWidth : IMAGE_MIN_SIZE;

      return {
        height: lockedHeight,
        width: lockedWidth,
        x: positionResizingRef?.current?.includes('left')
          ? lockedWidth > IMAGE_MIN_SIZE
            ? diff.x
            : wrapperWidth - IMAGE_MIN_SIZE
          : prev.x,
        y: positionResizingRef?.current?.includes('top')
          ? lockedHeight > IMAGE_MIN_SIZE
            ? diff.y
            : wrapperHeight - IMAGE_MIN_SIZE
          : prev.y,
      };
    });
  };

  const handleMouseUp = (e) => {
    e.stopPropagation();

    setActiveHandle(null);
    setResizedItemId(null);

    setResizeValues((prev) => {
      positionResizingRef?.current &&
        onChange({
          resizeDirection: positionResizingRef.current,
          value: { ...prev, y: 0 },
        });

      return { ...prev, y: 0 };
    });

    detachDocumentsEvents();
  };

  const attachDocumentEvents = () => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
  };

  const detachDocumentsEvents = () => {
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('mousemove', handleMouseMove);
  };

  const displayHandlers = isDesktopMode && isResizeActive && positionsHandler;

  return (
    <AbsoluteWrapper
      data-testid="resizable-image-wrapper"
      $isResizing={resizedItemId === item.id}
      $isResizeActive={isResizeActive}
      height={`${resizeValues?.height ?? dimensions?.height}px`}
      left={`${resizeValues?.x ?? position?.x}px`}
      onMouseLeave={handleMouseLeave}
      onMouseOver={handleMouseOver}
      ref={resizeRef}
      top={`${resizeValues?.y ?? position?.y}px`}
      width={`${resizeValues?.width ?? dimensions?.width}px`}
    >
      <AbsoluteImage
        data-testid="resizable-image"
        $borderRadius={item.attributes?.imageRadius}
        $imageSrc={item.attributes?._imageData || item.attributes?.imageSrc}
      >
        {displayHandlers &&
          positionsHandler.map((positionHandler) => (
            <Handlers
              activeHandle={activeHandle}
              key={positionHandler}
              onMouseDown={handleMouseDown(positionHandler)}
              positionHandler={positionHandler}
              ref={(node: HTMLDivElement) => {
                if (handlersRef?.current) {
                  handlersRef.current[positionHandler] = node;
                }
              }}
              resizeValues={resizeValues}
            />
          ))}
      </AbsoluteImage>
    </AbsoluteWrapper>
  );
};

export default ResizableImage;

ResizableImage.defaultProps = {};

const AbsoluteImage = styled(Box)<{ $borderRadius: number; $imageSrc: string }>`
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  position: absolute;
  border-radius: ${({ $borderRadius }) => $borderRadius || 0}px;
  background-image: url(${({ $imageSrc }) => $imageSrc});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const AbsoluteWrapper = styled(Box)<{
  $isResizeActive: boolean;
  $isResizing: boolean;
}>`
  outline: ${BORDER_WIDTH}px solid
    ${({ $isResizeActive }) => ($isResizeActive ? '#c4d2dd' : 'transparent')};
  outline-offset: -${BORDER_WIDTH}px;
  right: 0;
  bottom: 0;
  opacity: ${({ $isResizing }) => ($isResizing ? 0.7 : 1)};
  position: absolute;
  z-index: ${({ $isResizeActive }) => ($isResizeActive ? 300 : 1)};
`;
